import { getUser } from "./index.js";
import { getNationalIdInMemory } from "./userPiiMemory.js";

const normalizeCpf = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	const normalizedValue = String(value).replace(/\D/g, "");
	return normalizedValue || null;
};

const extractNationalIdFromAttributes = (attributes) => {
	if (!Array.isArray(attributes)) {
		return null;
	}

	const nationalIdAttribute = attributes.find((attribute) => {
		const name = attribute?.Name || attribute?.name;
		return name === "custom:nationalId" || name === "national_id";
	});

	return nationalIdAttribute?.Value ?? nationalIdAttribute?.value ?? null;
};

const emitAntifraudeFailure = (detail) => {
	window.dispatchEvent(new CustomEvent("antifraude-sdk-failure", { detail }));
};

const emitAntifraudeSuccess = () => {
	window.dispatchEvent(new CustomEvent("antifraude-sdk-success"));
};

const getUserData = () => {
	const user = getUser();

	if (!user) return null;

	const cpf = normalizeCpf(
		getNationalIdInMemory() ??
			user.national_id ??
			user.nationalId ??
			user.custom?.nationalId ??
			user["custom:nationalId"] ??
			user.attributes?.national_id ??
			user.attributes?.nationalId ??
			extractNationalIdFromAttributes(user.UserAttributes)
	);
	const email = user.email || user.attributes?.email;
	const username = user.username || user.Username;

	return {
		cpf,
		username,
		email,
	};
};

const sendAntifraudeAnalysis = (action) => {
	const token = window.ANTIFRAUDE_TOKEN;
	if (!token || token === "") {
		return;
	}

	const sdkInstance = window.__antifraudeSDKInstance;
	if (!sdkInstance) {
		return;
	}

	const userData = getUserData();
	if (!userData?.cpf || !userData?.username || !userData?.email) {
		if (!userData?.cpf) {
			emitAntifraudeFailure("GEOLOCATION_CPF_FAILED");
		}

		console.warn("[Antifraude SDK]: Analysis skipped due to missing user metadata", {
			action,
			hasCpf: !!userData?.cpf,
			hasUsername: !!userData?.username,
			hasEmail: !!userData?.email,
		});
		return false;
	}

	const refIdGeoloc = window.MULTINATIONAL_TOKEN
		? window.MULTINATIONAL_TOKEN
		: window.TENANT === "mars"
			? "marsbet"
			: window.TENANT;

	try {
		console.log("[Antifraude SDK]: Sending analysis");
		sdkInstance.setAction(action);

		const request = sdkInstance.sendAnalisys({
			username: userData.username,
			email: userData.email,
			cpf: userData.cpf,
			ref_id_geoloc: refIdGeoloc,
		});

		if (request && typeof request.then === "function") {
			request
				.then(async (response) => {
					let responseData = response;

					if (
						response &&
						typeof response.clone === "function" &&
						typeof response.json === "function"
					) {
						try {
							responseData = await response.clone().json();
						} catch (error) {
							responseData = null;
						}
					}

					const isFailedHttpResponse =
						response &&
						typeof response.ok === "boolean" &&
						typeof response.status === "number" &&
						(!response.ok || response.status === 401 || response.status === 403);

					if (isFailedHttpResponse) {
						if (!responseData) {
							emitAntifraudeFailure("GEOLOCATION_CHECK_BLOCKED");
							console.error(
								"[Antifraude SDK]: Analysis request failed (HTTP error)",
								{
									action,
									status: response?.status,
								}
							);
							return;
						}
						if (
							!responseData?.message &&
							(!Array.isArray(responseData?.errors) ||
								responseData.errors.length === 0)
						) {
							emitAntifraudeFailure("GEOLOCATION_CHECK_BLOCKED");
							console.error(
								"[Antifraude SDK]: Analysis request failed (HTTP error)",
								{
									action,
									status: response?.status,
								}
							);
							return;
						}
					}

					const message =
						typeof responseData?.message === "string"
							? responseData.message.toLowerCase()
							: "";
					const errors = [
						...(Array.isArray(responseData?.errors) ? responseData.errors : []),
						...(Array.isArray(responseData?.alerts) ? responseData.alerts : []),
					];
					const hasCpfError = errors.some(
						(errorMessage) =>
							typeof errorMessage === "string" &&
							errorMessage.toLowerCase().includes("cpf")
					);
					const isServiceLimited =
						response?.ok && responseData?.message && responseData?.body === null;

					if (hasCpfError || message.includes("cpf")) {
						emitAntifraudeFailure("GEOLOCATION_CPF_FAILED");
					}

					const hasUnauthorizedError =
						message === "unauthorized" ||
						errors.some(
							(err) =>
								typeof err === "string" &&
								(err.toLowerCase().includes("token") ||
									err.toLowerCase().includes("origin"))
						);

					if (hasUnauthorizedError || isServiceLimited) {
						emitAntifraudeFailure("GEOLOCATION_CHECK_BLOCKED");
					}

					if (
						message === "error" ||
						errors.length > 0 ||
						hasUnauthorizedError ||
						isServiceLimited
					) {
						console.error("[Antifraude SDK]: Analysis returned validation errors", {
							action,
							responseData,
						});
						return;
					}

					emitAntifraudeSuccess();
				})
				.catch((error) => {
					const errorMessage =
						typeof error?.message === "string" ? error.message.toLowerCase() : "";

					emitAntifraudeFailure(
						errorMessage.includes("cpf")
							? "GEOLOCATION_CPF_FAILED"
							: "GEOLOCATION_CHECK_BLOCKED"
					);
					console.error("[Antifraude SDK]: Analysis request failed", error);
				});
		}

		return true;
	} catch (error) {
		emitAntifraudeFailure("GEOLOCATION_CHECK_BLOCKED");
		console.error("[Antifraude SDK]: Failed to send analysis", error);
		return false;
	}
};

export const sendSignin = () => sendAntifraudeAnalysis("signin");
export const sendSignup = () => sendAntifraudeAnalysis("signup");
export const sendDeposit = () => sendAntifraudeAnalysis("deposit");
export const sendWithdraw = () => sendAntifraudeAnalysis("withdraw");
export const sendCheck = () => sendAntifraudeAnalysis("check");
